import { useRef } from 'react';
import { eventBannerThumb } from '../../../utils/media';

// ---- console-boot / disc-launch palette (deep navy + electric blue) ----
const NAVY = '#05091a';        // deepest card background
const NAVY_HI = '#0a1430';     // raised panel / thumbnail backdrop
const BLUE = '#2f86ff';        // electric blue accent
const BLUE_HI = '#4aa0ff';     // brighter blue (glows / strokes)
const LIGHT = '#8fb4ff';       // light-blue body text
const DIM = '#4a6aa0';         // dim label text
const INK = '#dbe8ff';         // near-white ink

const CARD_HEIGHT = 210;

let _gidc = 0;

const fmtDate = (d) =>
  (d ? new Date(d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const fmtTime = (d) =>
  (d ? new Date(d).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '');

// ---- small inline glyphs (generic — no trademarked marks) ----
function GlobeIcon({ size = 11, color = DIM }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx={12} cy={12} r={9} fill="none" stroke={color} strokeWidth={1.4} />
      <line x1={3} y1={12} x2={21} y2={12} stroke={color} strokeWidth={1.1} />
      <path d="M12 3 C7 7 7 17 12 21 M12 3 C17 7 17 17 12 21" fill="none" stroke={color} strokeWidth={1.1} />
    </svg>
  );
}

// Abstract corner glyph (ring + chevron) — NOT the face-button cluster.
function NodeGlyph({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx={7} cy={12} r={4} fill="none" stroke={BLUE_HI} strokeWidth={1.4} />
      <path d="M 15 7 L 20 12 L 15 17" fill="none" stroke={BLUE_HI} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Right-cluster HUD nodes: a few uneven bars + 3 dots.
function HudNodes() {
  return (
    <svg width={36} height={12} viewBox="0 0 36 12" xmlns="http://www.w3.org/2000/svg">
      <g fill={BLUE_HI} opacity={0.85}>
        <rect x={0} y={1} width={3} height={9} />
        <rect x={5} y={3} width={3} height={7} />
        <rect x={10} y={0} width={3} height={10} />
      </g>
      <g fill={LIGHT}>
        <circle cx={20} cy={5} r={1.6} />
        <circle cx={26} cy={5} r={1.6} />
        <circle cx={32} cy={5} r={1.6} />
      </g>
    </svg>
  );
}

// Faint blue grid + CRT scanlines + layered outer edge glow (no SVG filters;
// glow faked with stacked low-opacity strokes + a radial wash). Stretches with
// the card via preserveAspectRatio="none" — only stretch-tolerant marks.
function CardHud({ g }) {
  const W = 360, H = CARD_HEIGHT;
  const vlines = []; for (let x = 40; x < W; x += 40) vlines.push(x);
  const hlines = []; for (let y = 40; y < H; y += 40) hlines.push(y);
  const scan = []; for (let y = 6; y < H; y += 8) scan.push(y);
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
      <defs>
        <radialGradient id={`${g}edge`} cx="0.5" cy="0.5" r="0.72">
          <stop offset="0.6" stopColor={BLUE} stopOpacity="0" />
          <stop offset="1" stopColor={BLUE} stopOpacity="0.22" />
        </radialGradient>
      </defs>
      {/* faint grid */}
      <g opacity={0.5}>
        {vlines.map((x) => <line key={`v${x}`} x1={x} y1={0} x2={x} y2={H} stroke={BLUE} strokeWidth={0.6} opacity={0.14} />)}
        {hlines.map((y) => <line key={`h${y}`} x1={0} y1={y} x2={W} y2={y} stroke={BLUE} strokeWidth={0.6} opacity={0.14} />)}
      </g>
      {/* CRT scanlines */}
      <g opacity={0.05}>
        {scan.map((y) => <line key={`s${y}`} x1={0} y1={y} x2={W} y2={y} stroke={LIGHT} strokeWidth={1} />)}
      </g>
      {/* outer edge glow — layered low-opacity strokes + radial wash */}
      <rect x={1} y={1} width={W - 2} height={H - 2} rx={11} fill="none" stroke={BLUE} strokeWidth={2} opacity={0.18} />
      <rect x={2.5} y={2.5} width={W - 5} height={H - 5} rx={10} fill="none" stroke={BLUE_HI} strokeWidth={1} opacity={0.35} />
      <rect x={0.6} y={0.6} width={W - 1.2} height={H - 1.2} rx={11.5} fill={`url(#${g}edge)`} />
    </svg>
  );
}

// The right media-window decorations: beveled panel outline, grid overlay and
// corner brackets — all drawn AROUND/over the chrome, never blocking the image
// (image sits behind, panel art has no opaque fill over the photo area).
function MediaChrome({ w, h }) {
  // panel inset coords inside the slot (slot is w x h)
  const bx = 6, bb = 6; // bevel cut
  const top = 0, bot = h, left = 0, right = w;
  const panel = `M ${left + bx} ${top} L ${right} ${top} L ${right} ${bot - bb} L ${right - bx} ${bot} L ${left} ${bot} L ${left} ${top + bb} Z`;
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
      {/* beveled outline */}
      <path d={panel} fill="none" stroke={BLUE} strokeWidth={1} opacity={0.45} />
      {/* faint grid overlay */}
      <g stroke={BLUE} strokeWidth={0.5} opacity={0.12}>
        <line x1={w * 0.33} y1={2} x2={w * 0.33} y2={h - 2} />
        <line x1={w * 0.66} y1={2} x2={w * 0.66} y2={h - 2} />
        <line x1={2} y1={h * 0.34} x2={w - 2} y2={h * 0.34} />
        <line x1={2} y1={h * 0.67} x2={w - 2} y2={h * 0.67} />
      </g>
      {/* corner brackets */}
      <g stroke={BLUE_HI} strokeWidth={1.4} fill="none">
        <path d={`M 4 14 L 4 6 L 12 6`} />
        <path d={`M ${w - 12} 6 L ${w - 4} 6 L ${w - 4} 14`} />
        <path d={`M 4 ${h - 14} L 4 ${h - 6} L 12 ${h - 6}`} />
        <path d={`M ${w - 12} ${h - 6} L ${w - 4} ${h - 6} L ${w - 4} ${h - 14}`} />
      </g>
    </svg>
  );
}

// Faint blue city-skyline placeholder (lines + a few window dots).
function SkylinePlaceholder({ w, h }) {
  return (
    <svg width="100%" height="100%" viewBox="0 0 120 130" preserveAspectRatio="xMidYMax meet" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
      <path
        d="M 8 122 L 8 84 L 26 84 L 26 58 L 44 58 L 44 96 L 64 96 L 64 46 L 84 46 L 84 84 L 102 84 L 102 70 L 114 70 L 114 122 Z"
        fill="none" stroke={BLUE_HI} strokeWidth={1.2} opacity={0.4}
      />
      <g fill={LIGHT} opacity={0.5}>
        <rect x={14} y={92} width={2} height={2} /><rect x={32} y={66} width={2} height={2} />
        <rect x={70} y={54} width={2} height={2} /><rect x={90} y={92} width={2} height={2} />
      </g>
    </svg>
  );
}

// A full custom card layout in a glossy deep-blue console boot / disc-launch style.
export default function BootHudLayout({ event }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `bh${++_gidc}_`;
  const g = idRef.current;

  const dateStr = event?.eventDate ? fmtDate(event.eventDate) : 'DATE TBA';
  const timeStr = event?.eventDate ? fmtTime(event.eventDate) : '';
  const dateLabel = (dateStr + (timeStr ? `  ${timeStr}` : '')).toUpperCase();
  const cat = (event?.category || 'EVENT').toString().toUpperCase();
  const title = (event?.title || 'UNTITLED EVENT').toUpperCase();
  const address = event?.address || 'Add a location';
  const going = event?.attendeeCount ?? 0;
  const interested = event?.interestedCount ?? 0;
  const countLabel = `${going} GOING . ${interested} INTERESTED`;
  const myStatus = event?.myStatus || null;

  return (
    <div style={styles.card}>
      <CardHud g={g} />

      {/* ===== TOP BAR ===== */}
      <div style={styles.topbar}>
        <div style={styles.wordChip}>
          <span style={styles.wordChipText}>AXIS</span>
        </div>
        <div style={styles.loadWrap}>
          <div style={styles.loadTrack}>
            <div style={styles.loadFill} />
          </div>
          <span style={styles.loadLabel}>EVENT CARD</span>
        </div>
        <div style={styles.topRight}>
          <HudNodes />
          <NodeGlyph size={20} />
        </div>
      </div>
      <div style={styles.topRule} />

      {/* ===== BODY ROW ===== */}
      <div style={styles.row}>
        {/* ---- LEFT MAIN COLUMN ---- */}
        <div style={styles.main}>
          <div style={styles.catPill}>
            <span style={{ ...styles.catText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat}</span>
          </div>

          <span style={{ ...styles.title, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>{title}</span>
          <div style={styles.titleRule} />

          <span style={{ ...styles.metaText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{countLabel}</span>

          <div style={styles.datePill}>
            <span style={{ ...styles.datePillText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dateLabel}</span>
          </div>
          <div style={styles.locPill}>
            <span style={{ ...styles.locPillText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{address}</span>
          </div>

          <div style={styles.rsvpRow}>
            <div style={{ ...styles.rsvpPill, ...(myStatus === 'attending' ? styles.rsvpPillActive : {}) }}>
              <span style={{ ...styles.rsvpText, ...(myStatus === 'attending' ? styles.rsvpTextActive : {}) }}>GOING</span>
            </div>
            <div style={{ ...styles.rsvpPill, ...(myStatus === 'interested' ? styles.rsvpPillActive : {}) }}>
              <span style={{ ...styles.rsvpText, ...(myStatus === 'interested' ? styles.rsvpTextActive : {}) }}>INTERESTED</span>
            </div>
          </div>
        </div>

        {/* ---- RIGHT MEDIA WINDOW ---- */}
        <div style={styles.mediaWrap}>
          <div style={styles.mediaSlot}>
            {event?.bannerImageUrl ? (
              <img src={eventBannerThumb(event)} alt="" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={styles.mediaPlaceholder}>
                <SkylinePlaceholder w={126} h={130} />
              </div>
            )}
            <MediaChrome w={126} h={130} />
          </div>
        </div>
      </div>

      {/* ---- LEFT EDGE VERTICAL TEXT ---- */}
      <div style={styles.leftRail}>
        <span style={{ ...styles.leftRailText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>EVENT SYSTEM 2000</span>
      </div>
      {/* ---- RIGHT EDGE VERTICAL TAG ---- */}
      <div style={styles.rightRail}>
        <span style={{ ...styles.rightRailText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>EV . 2</span>
      </div>

      {/* ===== BOTTOM BAR (right of the RSVP pills, never over them) ===== */}
      <div style={styles.bottomBar}>
        <div style={styles.regionBox}><span style={styles.regionText}>NTSC</span></div>
        <div style={styles.regionBox}><span style={styles.regionText}>U/C</span></div>
        <span style={styles.tagline}>LIVE . CONNECT . PLAY</span>
        <span style={styles.bottomCode}>00-02</span>
        <GlobeIcon size={11} color={DIM} />
      </div>
    </div>
  );
}

const styles = {
  card: { display: 'flex', flexDirection: 'column', position: 'relative', width: '100%', height: CARD_HEIGHT, borderRadius: 12, overflow: 'hidden', backgroundColor: NAVY_HI, border: `1px solid ${BLUE + '44'}` },

  // top bar
  topbar: { display: 'flex', flexDirection: 'row', height: 22, alignItems: 'center', paddingLeft: 10, paddingRight: 10, marginTop: 6 },
  wordChip: { display: 'flex', flexDirection: 'column', borderWidth: 1, borderStyle: 'solid', borderColor: BLUE_HI + '99', borderRadius: 3, paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2 },
  wordChipText: { color: INK, fontSize: 8, fontWeight: '700', letterSpacing: 1.5 },
  loadWrap: { display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center', justifyContent: 'center', paddingLeft: 8, paddingRight: 8 },
  loadTrack: { display: 'flex', flexDirection: 'column', width: 104, height: 5, borderRadius: 2.5, backgroundColor: NAVY, borderWidth: 0.5, borderStyle: 'solid', borderColor: BLUE + '80', justifyContent: 'center' },
  loadFill: { display: 'flex', flexDirection: 'column', width: '62%', height: 3, borderRadius: 1.5, backgroundColor: BLUE_HI, marginLeft: 1 },
  loadLabel: { color: LIGHT, fontSize: 6.5, fontWeight: '700', letterSpacing: 2, marginTop: 3 },
  topRight: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 },
  topRule: { display: 'flex', flexDirection: 'column', height: 1, marginLeft: 10, marginRight: 10, backgroundColor: BLUE + '40' },

  row: { display: 'flex', flexDirection: 'row', flex: 1, paddingTop: 7 },

  // left main column
  main: { display: 'flex', flexDirection: 'column', flex: 1, paddingLeft: 16, paddingRight: 10, justifyContent: 'flex-start' },
  catPill: { display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', borderWidth: 1, borderStyle: 'solid', borderColor: BLUE_HI + '80', backgroundColor: BLUE + '2e', borderRadius: 4, paddingLeft: 8, paddingRight: 8, paddingTop: 3, paddingBottom: 3 },
  catText: { color: INK, fontSize: 8, fontWeight: '800', letterSpacing: 1.5 },
  title: { color: INK, fontSize: 19, lineHeight: '21px', fontWeight: '800', letterSpacing: 0.5, marginTop: 7 },
  titleRule: { display: 'flex', flexDirection: 'column', height: 2, width: 36, borderRadius: 1, backgroundColor: BLUE_HI, marginTop: 6, marginBottom: 6 },
  metaText: { color: LIGHT, fontSize: 9, fontWeight: '600', letterSpacing: 0.4 },
  datePill: { display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', backgroundColor: BLUE, borderRadius: 9.5, paddingLeft: 11, paddingRight: 11, paddingTop: 4, paddingBottom: 4, marginTop: 8 },
  datePillText: { color: NAVY, fontSize: 8.5, fontWeight: '800', letterSpacing: 0.5 },
  locPill: { display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', borderWidth: 1, borderStyle: 'solid', borderColor: BLUE_HI + '99', borderRadius: 9.5, paddingLeft: 11, paddingRight: 11, paddingTop: 4, paddingBottom: 4, marginTop: 7 },
  locPillText: { color: INK, fontSize: 8.5, fontWeight: '700', letterSpacing: 0.4 },
  rsvpRow: { display: 'flex', flexDirection: 'row', gap: 7, marginTop: 9 },
  rsvpPill: { display: 'flex', flexDirection: 'column', borderWidth: 1, borderStyle: 'solid', borderColor: BLUE_HI + '80', borderRadius: 9, paddingLeft: 11, paddingRight: 11, paddingTop: 4, paddingBottom: 4 },
  rsvpPillActive: { borderColor: 'rgba(255,255,255,0.85)', backgroundColor: 'rgba(255,255,255,0.12)' },
  rsvpText: { color: LIGHT, fontSize: 8, fontWeight: '800', letterSpacing: 1 },
  rsvpTextActive: { color: '#ffffff' },

  // right media window
  mediaWrap: { display: 'flex', flexDirection: 'column', width: '36%', paddingRight: 14, paddingLeft: 4, justifyContent: 'flex-start' },
  mediaSlot: { display: 'flex', flexDirection: 'column', width: '100%', height: 130, backgroundColor: NAVY, borderRadius: 4, overflow: 'hidden', position: 'relative' },
  mediaPlaceholder: { display: 'flex', flexDirection: 'column', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' },

  // edge rails
  leftRail: { display: 'flex', flexDirection: 'column', position: 'absolute', left: -52, top: 100, width: 120, alignItems: 'center', transform: 'rotate(-90deg)' },
  leftRailText: { color: DIM, fontSize: 6, fontWeight: '800', letterSpacing: 2.5 },
  rightRail: { display: 'flex', flexDirection: 'column', position: 'absolute', right: -22, top: 96, width: 60, alignItems: 'center', transform: 'rotate(90deg)' },
  rightRailText: { color: DIM, fontSize: 9, fontWeight: '800', letterSpacing: 1 },

  // bottom bar — anchored right so it sits clear of the left RSVP pills
  bottomBar: { display: 'flex', flexDirection: 'row', position: 'absolute', left: 150, right: 12, bottom: 7, alignItems: 'center', gap: 6 },
  regionBox: { display: 'flex', flexDirection: 'column', borderWidth: 1, borderStyle: 'solid', borderColor: DIM + 'b3', borderRadius: 2, paddingLeft: 3, paddingRight: 3, paddingTop: 1.5, paddingBottom: 1.5 },
  regionText: { color: LIGHT, fontSize: 6.5, fontWeight: '800', letterSpacing: 0.5 },
  tagline: { flex: 1, textAlign: 'center', color: DIM, fontSize: 6.5, fontWeight: '800', letterSpacing: 1.5 },
  bottomCode: { color: LIGHT, fontSize: 6.5, fontWeight: '800', letterSpacing: 0.5 },
};
